import React, { useEffect, useState, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { API, AuthContext } from '../App';
import Navbar from '../components/Navbar';
import SendAsMeModal from '../components/SendAsMeModal';
import { ArrowLeft, Eye, Calendar, Award, Clock, MapPin, Shield, User as UserIcon, Send } from 'lucide-react';
import { parseLocalDate } from '../utils/dateTime';

/**
 * Admin-only "View as Participant" mode.
 *
 * Renders a read-only snapshot of what a participant sees — their profile summary,
 * their sign-ups, and their hours. It does NOT change the admin's JWT or role;
 * all data is fetched through the admin endpoint /admin/participants/{user_id}.
 *
 * Entry point: /admin/participants/:id/view-as
 * Exit:        Navbar's normal admin navigation, or the "Exit View-as Mode" banner button.
 */
const STATUS_BADGE = {
  pending: 'badge badge-pending',
  approved: 'badge badge-approved',
  declined: 'badge',
  attended: 'badge badge-approved',
  no_show: 'badge',
  cancelled: 'badge',
};

const statusLabel = (s) => {
  if (!s) return '';
  return s.charAt(0).toUpperCase() + s.slice(1).replace(/_/g, ' ');
};

const AdminViewAsParticipant = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [messageSignup, setMessageSignup] = useState(null); // signup to message about
  const [messageInitialKey, setMessageInitialKey] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/participants/${id}`);
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load participant');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Hard guard — block non-admins from this route even if the URL is shared.
  if (user && user.role !== 'admin') {
    return (
      <div className="min-h-screen bg-[#FBF4EA]">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <p className="text-[#888]">Not authorized.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBF4EA]">
        <Navbar />
        <div className="flex items-center justify-center py-20"><div className="text-lg text-[#888]">Loading…</div></div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#FBF4EA]">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <p className="text-[#888]">Participant not found.</p>
        </div>
      </div>
    );
  }

  const signups = data.signups || [];
  const hourRequests = data.hour_requests || [];

  // Split signups by upcoming / past using opportunity_date
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const upcomingSignups = signups.filter(s => {
    try { return parseLocalDate(s.opportunity_date) >= today; } catch { return true; }
  });
  const pastSignups = signups.filter(s => {
    try { return parseLocalDate(s.opportunity_date) < today; } catch { return false; }
  });

  const pendingHours = hourRequests.filter(h => h.status === 'pending');
  const approvedHours = hourRequests.filter(h => h.status === 'approved');

  return (
    <div className="min-h-screen bg-[#FBF4EA]" data-testid="view-as-participant-page">
      {/* Persistent banner — clearly indicates view-only impersonation */}
      <div
        className="bg-[#8B2E3C] text-white px-4 py-3 sticky top-0 z-40 shadow-md"
        data-testid="view-as-banner"
      >
        <div className="max-w-6xl mx-auto flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <div className="flex items-center gap-2 text-sm">
            <Eye className="w-4 h-4" />
            <span>
              Viewing as <strong>{data.name}</strong> ({data.email}) —{' '}
              <span className="italic opacity-90">read-only. Your admin role is unchanged.</span>
            </span>
          </div>
          <button
            onClick={() => navigate(`/admin/participants/${id}`)}
            className="bg-white text-[#8B2E3C] text-sm font-semibold px-4 py-1.5 rounded-md hover:bg-[#FBF4EA] self-start sm:self-auto"
            data-testid="exit-view-as-button"
          >
            Exit View-as Mode
          </button>
        </div>
      </div>

      <Navbar />

      <div className="max-w-6xl mx-auto px-4 py-6 sm:py-8">
        <button
          onClick={() => navigate(`/admin/participants/${id}`)}
          className="flex items-center gap-2 text-[#8B2E3C] font-medium mb-4 hover:underline"
          data-testid="view-as-back-button"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Admin Participant Detail
        </button>

        {/* Profile summary (mirrors VolunteerProfile's headline) */}
        <div className="card mb-6" data-testid="view-as-profile-card">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#8B2E3C] text-white flex items-center justify-center">
                <UserIcon className="w-6 h-6" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-[#222]">{data.name}</h1>
                <p className="text-sm text-[#666]">{data.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="bg-white px-4 py-2 rounded-lg border border-[#E9DDCF]">
                <div className="text-xs text-[#888]">Total Approved Hours</div>
                <div className="text-xl font-bold text-[#8B2E3C]" data-testid="view-as-total-hours">
                  {(data.completed_hours || 0).toFixed(1)}h
                </div>
              </div>
              {data.clearance_status && (
                <div className="bg-white px-4 py-2 rounded-lg border border-[#E9DDCF] flex items-center gap-2">
                  <Shield className="w-4 h-4 text-[#8B2E3C]" />
                  <div>
                    <div className="text-xs text-[#888]">Status</div>
                    <div className="text-sm font-semibold text-[#222] capitalize">{data.clearance_status.replace(/_/g, ' ')}</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* === MY SIGN-UPS — mirrors MySignups.js layout === */}
          <section>
            <h2 className="text-2xl font-bold text-[#222] mb-4">Upcoming Sign-ups</h2>
            {upcomingSignups.length === 0 ? (
              <div className="card text-center py-8" data-testid="view-as-no-upcoming-signups">
                <p className="text-[#666]">No upcoming commitments.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {upcomingSignups.map((s) => (
                  <div key={s.id} className="card" data-testid={`view-as-upcoming-signup-${s.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-[#222]">{s.opportunity_title}</h3>
                        <div className="flex flex-wrap gap-3 text-sm text-[#666] mt-1">
                          <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{format(parseLocalDate(s.opportunity_date), 'MMM d, yyyy')}</span>
                          {s.opportunity_location && (
                            <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" />{s.opportunity_location}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <span className={STATUS_BADGE[s.status] || 'badge'}>{statusLabel(s.status)}</span>
                        <button
                          type="button"
                          onClick={() => { setMessageSignup(s); setMessageInitialKey('reminder'); }}
                          className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-[#8B2E3C] text-[#8B2E3C] hover:bg-[#FBF4EA] font-medium"
                          data-testid={`remind-them-button-${s.id}`}
                        >
                          <Send className="w-3 h-3" /> Remind them
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <h2 className="text-2xl font-bold text-[#222] mb-4 mt-8">Past Sign-ups</h2>
            {pastSignups.length === 0 ? (
              <div className="card text-center py-8" data-testid="view-as-no-past-signups">
                <p className="text-[#666]">No past sign-ups.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pastSignups.slice(0, 20).map((s) => {
                  const attended = s.status === 'attended' || (typeof s.hours_credited === 'number' && s.hours_credited > 0);
                  const msgKey = attended ? 'thankyou' : 'followup';
                  const msgLabel = attended ? 'Thank them' : 'Follow up';
                  return (
                  <div key={s.id} className="card" data-testid={`view-as-past-signup-${s.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-base font-semibold text-[#222]">{s.opportunity_title}</h3>
                        <div className="flex flex-wrap gap-3 text-sm text-[#666] mt-1">
                          <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" />{format(parseLocalDate(s.opportunity_date), 'MMM d, yyyy')}</span>
                          {typeof s.hours_credited === 'number' && s.hours_credited > 0 && (
                            <span className="flex items-center gap-1"><Award className="w-3.5 h-3.5 text-[#C9A227]" />{s.hours_credited}h credited</span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2 shrink-0">
                        <span className={STATUS_BADGE[s.status] || 'badge'}>{statusLabel(s.status)}</span>
                        <button
                          type="button"
                          onClick={() => { setMessageSignup(s); setMessageInitialKey(msgKey); }}
                          className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-md border border-[#8B2E3C] text-[#8B2E3C] hover:bg-[#FBF4EA] font-medium"
                          data-testid={`${msgKey === 'thankyou' ? 'thank-them' : 'follow-up'}-button-${s.id}`}
                        >
                          <Send className="w-3 h-3" /> {msgLabel}
                        </button>
                      </div>
                    </div>
                  </div>
                  );
                })}
                {pastSignups.length > 20 && (
                  <p className="text-xs text-[#888] text-center">Showing 20 of {pastSignups.length} past sign-ups.</p>
                )}
              </div>
            )}
          </section>

          {/* === MY HOURS — mirrors MyHours.js layout === */}
          <section>
            <h2 className="text-2xl font-bold text-[#222] mb-4">Pending Hour Requests</h2>
            {pendingHours.length === 0 ? (
              <div className="card text-center py-8" data-testid="view-as-no-pending-hours">
                <p className="text-[#666]">No pending hour requests.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {pendingHours.map((h) => (
                  <div key={h.id} className="card" data-testid={`view-as-pending-hours-${h.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-base font-semibold text-[#222]">{h.opportunity_title}</h3>
                        <div className="flex flex-col gap-1 text-sm text-[#666] mt-1">
                          <span className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5" />{format(parseLocalDate(h.opportunity_date || h.service_date), 'MMM d, yyyy')}</span>
                          <span className="flex items-center gap-2"><Award className="w-3.5 h-3.5" />{h.credit_hours} hours requested</span>
                        </div>
                      </div>
                      <span className="badge badge-pending">Pending</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            <h2 className="text-2xl font-bold text-[#222] mb-4 mt-8">Approved Hours</h2>
            {approvedHours.length === 0 ? (
              <div className="card text-center py-8" data-testid="view-as-no-approved-hours">
                <p className="text-[#666]">No approved hours yet.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {approvedHours.map((h) => (
                  <div key={h.id} className="card" data-testid={`view-as-approved-hours-${h.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <h3 className="text-base font-semibold text-[#222]">{h.opportunity_title}</h3>
                        <div className="flex flex-col gap-1 text-sm text-[#666] mt-1">
                          <span className="flex items-center gap-2"><Calendar className="w-3.5 h-3.5" />{format(parseLocalDate(h.opportunity_date || h.service_date), 'MMM d, yyyy')}</span>
                          <span className="flex items-center gap-2"><Award className="w-3.5 h-3.5 text-[#C9A227]" /><strong className="text-[#222]">{h.credit_hours}h</strong> approved</span>
                          {h.manual_entry && h.entered_by_name && (
                            <span className="text-xs text-[#888] italic flex items-center gap-2"><Clock className="w-3 h-3" />Manually credited by {h.entered_by_name}{h.reason ? ` — ${h.reason}` : ''}</span>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className="badge badge-approved">Approved</span>
                        {h.manual_entry && (
                          <span className="text-xs px-2 py-0.5 rounded-full font-medium bg-blue-100 text-blue-800">Admin/Partner Added</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>

        {/* Footer re-stating mode + exit */}
        <div className="mt-8 p-4 bg-white border border-[#E9DDCF] rounded-lg text-center text-sm text-[#666]">
          You're viewing this page as <strong>{data.name}</strong>. No changes have been made to your admin account.{' '}
          <button
            onClick={() => navigate(`/admin/participants/${id}`)}
            className="text-[#8B2E3C] hover:underline font-medium"
            data-testid="view-as-footer-exit-button"
          >
            Return to Admin Participant Detail
          </button>
        </div>
      </div>

      <SendAsMeModal
        isOpen={!!messageSignup}
        onClose={() => { setMessageSignup(null); setMessageInitialKey(null); }}
        participant={data}
        signup={messageSignup}
        initialKey={messageInitialKey}
      />
    </div>
  );
};

export default AdminViewAsParticipant;
